import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createLogger } from "../../../../lib/logger";
import { useAppStore } from "../../../../store/useAppStore";
import { Service } from "../../../../types";

const log = createLogger("useProjectServices");

export interface UseProjectServicesResult {
  services: Service[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useProjectServices(
  projectId: string,
  selectedEnvironmentId?: string
): UseProjectServicesResult {
  const { fetchServices } = useAppStore();
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // When projectId transitions from "" (disabled) to a real ID, set loading=true
  // synchronously before the browser paints so there's no single-frame flash of
  // an empty list between environment resolution and the fetch starting.
  const prevProjectIdRef = useRef(projectId);
  useLayoutEffect(() => {
    if (projectId && !prevProjectIdRef.current) {
      setLoading(true);
    }
    prevProjectIdRef.current = projectId;
  }, [projectId]);

  const fetchProjectServices = useCallback(async () => {
    if (!projectId) {
      setServices([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const fetchedServices = await fetchServices(
        projectId,
        selectedEnvironmentId
      );
      setServices(fetchedServices);
    } catch (err) {
      log.error("Failed to fetch project services", err);
      setError(err instanceof Error ? err.message : "Failed to fetch services");
    } finally {
      setLoading(false);
    }
  }, [projectId, selectedEnvironmentId, fetchServices]);

  useEffect(() => {
    fetchProjectServices();
  }, [fetchProjectServices]);

  return {
    services,
    loading,
    error,
    refetch: fetchProjectServices,
  };
}
